import { useTranslation } from 'react-i18next';
import SignatureCanvas from '../SignatureCanvas';

interface SigningMethodSelectorProps {
  method: 'canvas' | 'code';
  onMethodChange: (method: 'canvas' | 'code') => void;
  signatureData: string;
  onSignatureChange: (data: string) => void;
  verificationCode: string;
  enteredCode: string;
  onEnteredCodeChange: (code: string) => void;
}

export function SigningMethodSelector({
  method,
  onMethodChange,
  signatureData: _signatureData,
  onSignatureChange,
  verificationCode,
  enteredCode,
  onEnteredCodeChange,
}: SigningMethodSelectorProps) {
  const { t } = useTranslation();

  return (
    <>
      {/* Method Selection */}
      <div className="border border-gray-200 rounded-lg p-4">
        <h3 className="font-medium text-gray-900 mb-3">
          {t('signModal.signingMethod.heading')}
        </h3>

        {/* Canvas Method */}
        <label className="flex items-start gap-3 mb-4 cursor-pointer">
          <input
            type="radio"
            checked={method === 'canvas'}
            onChange={() => onMethodChange('canvas')}
            className="mt-1"
          />
          <div className="flex-1">
            <span className="font-medium">{t('signModal.signingMethod.canvas.title')}</span>
            <p className="text-sm text-gray-600 mt-1">
              {t('signModal.signingMethod.canvas.description')}
            </p>
          </div>
        </label>

        {/* Code Method */}
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="radio"
            checked={method === 'code'}
            onChange={() => onMethodChange('code')}
            className="mt-1"
          />
          <div className="flex-1">
            <span className="font-medium">{t('signModal.signingMethod.code.title')}</span>
            <p className="text-sm text-gray-600 mt-1">
              {t('signModal.signingMethod.code.description')}
            </p>
          </div>
        </label>
      </div>

      {/* Canvas Signature */}
      {method === 'canvas' && (
        <div>
          <label className="label">{t('signModal.signHere')}</label>
          <SignatureCanvas
            onChange={onSignatureChange}
            onClear={() => onSignatureChange('')}
          />
        </div>
      )}

      {/* Code Verification */}
      {method === 'code' && (
        <div className="space-y-3">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800 mb-2">
              <strong>{t('signModal.signingMethod.code.yourCode')}</strong>
            </p>
            <div className="text-2xl font-mono font-bold text-blue-900 tracking-wider">
              {verificationCode}
            </div>
            <p className="text-xs text-blue-600 mt-2">
              {t('signModal.signingMethod.code.enterCodeBelow')}
            </p>
          </div>

          <div>
            <label className="label">{t('signModal.signingMethod.code.inputLabel')}</label>
            <input
              type="text"
              className="input font-mono text-lg tracking-wider uppercase"
              value={enteredCode}
              onChange={(e) => onEnteredCodeChange(e.target.value.toUpperCase())}
              placeholder={t('signModal.signingMethod.code.placeholder')}
              maxLength={9}
            />
          </div>
        </div>
      )}
    </>
  );
}
